// Drawing App - HSV Color Picker Component

export class ColorPicker {
  private container: HTMLElement;
  private hue = 0;
  private saturation = 1;
  private value = 1;
  private recentColors: string[] = [];
  private maxRecent = 12;
  private onChangeCallback: ((color: string) => void) | null = null;

  private wheelCanvas: HTMLCanvasElement | null = null;
  private svCanvas: HTMLCanvasElement | null = null;
  private isDraggingWheel = false;
  private isDraggingSV = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.render();
  }

  onChange(cb: (color: string) => void): void {
    this.onChangeCallback = cb;
  }

  getColor(): string {
    return this.hsvToHex(this.hue, this.saturation, this.value);
  }

  setColor(hex: string): void {
    const hsv = this.hexToHsv(hex);
    this.hue = hsv.h;
    this.saturation = hsv.s;
    this.value = hsv.v;
    this.updateUI();
  }

  addToRecent(color: string): void {
    this.recentColors = [color, ...this.recentColors.filter(c => c !== color)].slice(0, this.maxRecent);
    this.renderRecentColors();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="color-picker-widget" style="width: 200px; padding: 8px;">
        <div style="position: relative; width: 180px; height: 180px; margin: 0 auto 8px;">
          <canvas class="cp-wheel" width="180" height="180" style="position: absolute; top: 0; left: 0; cursor: crosshair;"></canvas>
        </div>
        <div style="position: relative; width: 180px; height: 120px; margin: 0 auto 8px;">
          <canvas class="cp-sv" width="180" height="120" style="cursor: crosshair; border-radius: 4px;"></canvas>
        </div>
        <div class="cp-preview" style="width: 100%; height: 24px; border-radius: 4px; margin-bottom: 8px; border: 1px solid #555;"></div>
        <div class="cp-hex-input" style="margin-bottom: 8px;">
          <input type="text" class="cp-hex" style="width: 100%; background: #333; color: #fff; border: 1px solid #555; border-radius: 4px; padding: 4px 8px; font-family: monospace; font-size: 12px;" value="#ff0000">
        </div>
        <div class="cp-recent" style="display: flex; flex-wrap: wrap; gap: 4px;"></div>
      </div>
    `;

    this.wheelCanvas = this.container.querySelector('.cp-wheel') as HTMLCanvasElement;
    this.svCanvas = this.container.querySelector('.cp-sv') as HTMLCanvasElement;

    this.drawWheel();
    this.drawSV();
    this.updateUI();
    this.bindEvents();
  }

  private drawWheel(): void {
    if (!this.wheelCanvas) return;
    const ctx = this.wheelCanvas.getContext('2d')!;
    const size = 180;
    const cx = size / 2;
    const cy = size / 2;
    const outerR = 85;
    const innerR = 65;

    ctx.clearRect(0, 0, size, size);

    // Draw hue ring
    for (let angle = 0; angle < 360; angle++) {
      const startAngle = (angle - 1) * Math.PI / 180;
      const endAngle = (angle + 1) * Math.PI / 180;
      ctx.beginPath();
      ctx.arc(cx, cy, outerR, startAngle, endAngle);
      ctx.arc(cx, cy, innerR, endAngle, startAngle, true);
      ctx.closePath();
      ctx.fillStyle = `hsl(${angle}, 100%, 50%)`;
      ctx.fill();
    }

    // Draw hue indicator
    const hueAngle = (this.hue - 90) * Math.PI / 180;
    const indicatorR = (outerR + innerR) / 2;
    const ix = cx + Math.cos(hueAngle) * indicatorR;
    const iy = cy + Math.sin(hueAngle) * indicatorR;
    ctx.beginPath();
    ctx.arc(ix, iy, 6, 0, Math.PI * 2);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(ix, iy, 5, 0, Math.PI * 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  private drawSV(): void {
    if (!this.svCanvas) return;
    const ctx = this.svCanvas.getContext('2d')!;
    const width = 180;
    const height = 120;

    // Draw saturation-value gradient
    const hueColor = this.hsvToHex(this.hue, 1, 1);

    // White to hue color (horizontal)
    const gradH = ctx.createLinearGradient(0, 0, width, 0);
    gradH.addColorStop(0, '#ffffff');
    gradH.addColorStop(1, hueColor);
    ctx.fillStyle = gradH;
    ctx.fillRect(0, 0, width, height);

    // Transparent to black (vertical)
    const gradV = ctx.createLinearGradient(0, 0, 0, height);
    gradV.addColorStop(0, 'rgba(0,0,0,0)');
    gradV.addColorStop(1, '#000000');
    ctx.fillStyle = gradV;
    ctx.fillRect(0, 0, width, height);

    // Draw SV indicator
    const sx = this.saturation * width;
    const sy = (1 - this.value) * height;
    ctx.beginPath();
    ctx.arc(sx, sy, 6, 0, Math.PI * 2);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(sx, sy, 5, 0, Math.PI * 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  private updateUI(): void {
    this.drawWheel();
    this.drawSV();
    const color = this.getColor();
    const preview = this.container.querySelector('.cp-preview') as HTMLElement;
    if (preview) preview.style.backgroundColor = color;
    const hexInput = this.container.querySelector('.cp-hex') as HTMLInputElement;
    if (hexInput) hexInput.value = color;
  }

  private bindEvents(): void {
    if (this.wheelCanvas) {
      this.wheelCanvas.addEventListener('pointerdown', (e) => {
        const rect = this.wheelCanvas!.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (this.isOnWheel(x, y)) {
          this.isDraggingWheel = true;
          this.updateHueFromPos(x, y);
          this.wheelCanvas!.setPointerCapture(e.pointerId);
        }
      });
      this.wheelCanvas.addEventListener('pointermove', (e) => {
        if (!this.isDraggingWheel) return;
        const rect = this.wheelCanvas!.getBoundingClientRect();
        this.updateHueFromPos(e.clientX - rect.left, e.clientY - rect.top);
      });
      this.wheelCanvas.addEventListener('pointerup', () => {
        this.isDraggingWheel = false;
        this.addToRecent(this.getColor());
      });
    }

    if (this.svCanvas) {
      this.svCanvas.addEventListener('pointerdown', (e) => {
        this.isDraggingSV = true;
        const rect = this.svCanvas!.getBoundingClientRect();
        this.updateSVFromPos(e.clientX - rect.left, e.clientY - rect.top);
        this.svCanvas!.setPointerCapture(e.pointerId);
      });
      this.svCanvas.addEventListener('pointermove', (e) => {
        if (!this.isDraggingSV) return;
        const rect = this.svCanvas!.getBoundingClientRect();
        this.updateSVFromPos(e.clientX - rect.left, e.clientY - rect.top);
      });
      this.svCanvas.addEventListener('pointerup', () => {
        this.isDraggingSV = false;
        this.addToRecent(this.getColor());
      });
    }

    const hexInput = this.container.querySelector('.cp-hex') as HTMLInputElement;
    if (hexInput) {
      hexInput.addEventListener('change', () => {
        const val = hexInput.value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(val)) {
          this.setColor(val);
          this.notifyChange();
        }
      });
    }
  }

  private isOnWheel(x: number, y: number): boolean {
    const cx = 90, cy = 90;
    const dist = Math.hypot(x - cx, y - cy);
    return dist >= 65 && dist <= 85;
  }

  private updateHueFromPos(x: number, y: number): void {
    const cx = 90, cy = 90;
    const angle = Math.atan2(y - cy, x - cx);
    this.hue = ((angle * 180 / Math.PI) + 90 + 360) % 360;
    this.updateUI();
    this.notifyChange();
  }

  private updateSVFromPos(x: number, y: number): void {
    this.saturation = Math.max(0, Math.min(1, x / 180));
    this.value = Math.max(0, Math.min(1, 1 - y / 120));
    this.updateUI();
    this.notifyChange();
  }

  private notifyChange(): void {
    if (this.onChangeCallback) {
      this.onChangeCallback(this.getColor());
    }
  }

  private renderRecentColors(): void {
    const container = this.container.querySelector('.cp-recent');
    if (!container) return;
    container.innerHTML = '';
    for (const color of this.recentColors) {
      const swatch = document.createElement('div');
      swatch.style.cssText = `width: 18px; height: 18px; background: ${color}; border-radius: 3px; cursor: pointer; border: 1px solid #555;`;
      swatch.addEventListener('click', () => {
        this.setColor(color);
        this.notifyChange();
      });
      container.appendChild(swatch);
    }
  }

  private hsvToHex(h: number, s: number, v: number): string {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0, g = 0, b = 0;

    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }

    const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private hexToHsv(hex: string): { h: number; s: number; v: number } {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;

    let h = 0;
    if (d !== 0) {
      if (max === r) h = 60 * (((g - b) / d) % 6);
      else if (max === g) h = 60 * ((b - r) / d + 2);
      else h = 60 * ((r - g) / d + 4);
    }
    if (h < 0) h += 360;

    const s = max === 0 ? 0 : d / max;
    return { h, s, v: max };
  }
}
